/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Check, ShieldCheck, Lock } from 'lucide-react';

export const Solutions: React.FC = () => {
  return (
    <section id="solutions" className="py-32 bg-white dark:bg-[#0A0B0F] overflow-hidden">
      <div className="container mx-auto px-6">
        
        {/* Benefit 1 */}
        <div className="flex flex-col lg:flex-row items-center gap-20 mb-32">
           <div className="lg:w-1/2">
              <div className="inline-block px-3 py-1 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-widest rounded-full mb-6">
                 Merchant Solutions
              </div>
              <h3 className="font-serif text-5xl text-[#0A0B0F] dark:text-white mb-6 leading-[1.1]">
                 A Point of Sale that <br/> <span className="text-gray-400 italic">actually serves you.</span>
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-lg mb-8 leading-relaxed max-w-md">
                 Traditional POS systems charge 3% fees and hold your money for days. GhostWallet settles instantly with practically zero network fees.
              </p>
              <ul className="space-y-4">
                 {[
                    "Zero monthly terminal fees",
                    "Integrated inventory management",
                    "QR Code & NFC Payments",
                    "Automated tax compliance reports"
                 ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 font-medium text-[#0A0B0F] dark:text-white">
                       <div className="w-5 h-5 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0"><Check size={12}/></div>
                       {item}
                    </li>
                 ))}
              </ul>
           </div>
           <div className="lg:w-1/2 w-full relative">
               <div className="relative bg-white dark:bg-[#15171C] p-8 rounded-[32px] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)] border border-stone-100 dark:border-white/10">
                  {/* Mock UI Element representing POS */}
                  <div className="space-y-6">
                      <div className="flex justify-between items-center border-b border-gray-100 dark:border-white/10 pb-4">
                          <div className="font-serif font-bold text-xl text-[#0A0B0F] dark:text-white">Order #1042</div>
                          <div className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full uppercase tracking-wide">Paid</div>
                      </div>
                      <div className="space-y-3">
                          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400"><span>Premium Coffee Blend x2</span><span className="font-bold text-black dark:text-white">$12.00</span></div>
                          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400"><span>Artisan Sourdough</span><span className="font-bold text-black dark:text-white">$8.50</span></div>
                      </div>
                      <div className="pt-4 flex justify-between font-bold text-2xl text-[#0A0B0F] dark:text-white">
                          <span>Total</span>
                          <span>$20.50</span>
                      </div>
                      <button className="w-full py-4 bg-[#0A0B0F] dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm hover:bg-black dark:hover:bg-gray-200 transition-colors shadow-lg">Print Receipt</button>
                  </div>
               </div>
           </div>
        </div>

        {/* Benefit 2 */}
        <div className="flex flex-col lg:flex-row-reverse items-center gap-20">
           <div className="lg:w-1/2">
              <div className="inline-block px-3 py-1 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-widest rounded-full mb-6">
                 Contractor Solutions
              </div>
              <h3 className="font-serif text-5xl text-[#0A0B0F] dark:text-white mb-6 leading-[1.1]">
                 Escrow contracts that <br/> <span className="text-gray-400 italic">eliminate trust issues.</span>
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-lg mb-8 leading-relaxed max-w-md">
                 Never worry about getting paid again. GhostWallet Escrow locks funds in a smart contract before you start working, releasing them as you hit milestones.
              </p>
              <ul className="space-y-4">
                 {[
                    "Milestone-based fund release",
                    "Impartial dispute resolution",
                    "Multi-signature security",
                    "Client deposit verification"
                 ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 font-medium text-[#0A0B0F] dark:text-white">
                       <div className="w-5 h-5 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center shrink-0"><Check size={12}/></div>
                       {item}
                    </li>
                 ))}
              </ul>
           </div>
           <div className="lg:w-1/2 w-full relative">
               
               <div className="relative bg-white dark:bg-[#15171C] p-8 rounded-[32px] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.08)] dark:shadow-[0_20px_60px_-10px_rgba(0,0,0,0.5)] border border-stone-100 dark:border-white/10 overflow-hidden">
                   
                   {/* Header */}
                   <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100 dark:border-white/10 relative z-10">
                       <div className="flex items-center gap-3">
                           <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center border border-emerald-200 dark:border-emerald-500/20">
                               <ShieldCheck size={20} className="text-emerald-600 dark:text-emerald-400"/>
                           </div>
                           <div>
                               <div className="font-bold text-lg tracking-tight text-[#0A0B0F] dark:text-white">Escrow #8921</div>
                               <div className="text-[10px] text-gray-500 uppercase tracking-widest flex items-center gap-1">
                                   <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div> Active
                               </div>
                           </div>
                       </div>
                       <div className="text-right">
                           <div className="text-gray-400 text-xs uppercase tracking-widest font-bold">Total Value</div>
                           <div className="font-serif text-xl font-bold text-[#0A0B0F] dark:text-white">$4,500.00</div>
                       </div>
                   </div>

                   {/* Milestones */}
                   <div className="space-y-6 relative z-10">
                       <div className="flex gap-4 opacity-50">
                            <div className="flex flex-col items-center">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-500/30 flex items-center justify-center text-emerald-600 dark:text-emerald-500">
                                    <Check size={12}/>
                                </div>
                                <div className="w-0.5 h-full bg-emerald-200 dark:bg-emerald-900/30 my-1"></div>
                            </div>
                            <div className="pb-4">
                                <div className="font-bold text-sm text-[#0A0B0F] dark:text-white">Initial Deposit</div>
                                <div className="text-xs text-gray-500">Released Nov 15</div>
                            </div>
                       </div>

                       <div className="flex gap-4">
                            <div className="flex flex-col items-center">
                                <div className="w-6 h-6 rounded-full bg-[#0A0B0F] dark:bg-white text-white dark:text-black flex items-center justify-center shadow-lg">
                                    <div className="w-2 h-2 bg-white dark:bg-black rounded-full"></div>
                                </div>
                                <div className="w-0.5 h-full bg-gray-100 dark:bg-white/10 my-1"></div>
                            </div>
                            <div className="pb-4 w-full">
                                <div className="font-bold text-sm text-[#0A0B0F] dark:text-white">Milestone 2: Design Approval</div>
                                <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium">Funds Locked</div>
                                <div className="mt-3 p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-100 dark:border-white/5 text-xs text-gray-600 dark:text-gray-300 flex justify-between items-center">
                                    <span>Release Amount</span>
                                    <span className="font-bold text-[#0A0B0F] dark:text-white">$1,250.00</span>
                                </div>
                            </div>
                       </div>

                       <div className="flex gap-4 opacity-30">
                            <div className="flex flex-col items-center">
                                <div className="w-6 h-6 rounded-full border-2 border-gray-200 dark:border-white/30"></div>
                            </div>
                            <div>
                                <div className="font-bold text-sm text-[#0A0B0F] dark:text-white">Final Delivery</div>
                            </div>
                       </div>
                   </div>

                   {/* Action Button */}
                   <button className="w-full mt-8 py-3 bg-[#0A0B0F] dark:bg-white text-white dark:text-black font-bold rounded-xl text-sm hover:opacity-90 transition-opacity shadow-lg flex items-center justify-center gap-2">
                       <Lock size={14} /> View Contract Details
                   </button>
               </div>
           </div>
        </div>

      </div>
    </section>
  );
};